import { HttpLink, split } from '@apollo/client'
import { type ErrorResponse, onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import type { ServerError } from '@apollo/client/link/utils'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'

import {
  BACKEND_BASE_URL,
  RETRY_REQUEST_INITIAL_DELAY,
  RETRY_REQUEST_MAX_DELAY,
  RETRY_WS_INITIAL_DELAY,
  RETRY_WS_MAX_DELAY,
  WS_BASE_URL,
  WS_KEEP_ALIVE,
} from '@/config.ts'
import { ERRORS, ERROR_MESSAGES } from '@/constants/ERRORS.ts'
import { PUBLIC_PATHS } from '@/constants/PUBLIC_PATHS.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { report } from '@/utils/sentry.ts'

export const errorHandler = ({ graphQLErrors, networkError, operation }: ErrorResponse) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.path?.includes('project') && err.extensions?.code === ERRORS.NOT_FOUND) {
        window.location.replace('/projects')
      } else if (err.extensions?.code === 'FORBIDDEN') {
        GlobalPubSub.publish(GlobalPubSub.Signals.DISPLAY_SYSTEM_MESSAGE_MODAL, ERROR_MESSAGES.FORBIDDEN)
      } else {
        report(
          err,
          { operationName: operation?.operationName },
          { query: operation?.query, variables: operation?.variables },
        )
      }
    }
  }

  if ((networkError as ServerError)?.response?.status === 403) {
    GlobalPubSub.publish(GlobalPubSub.Signals.DISPLAY_SYSTEM_MESSAGE_MODAL, ERROR_MESSAGES.FORBIDDEN)
  }

  if ((networkError as ServerError)?.response?.status === 401 && !PUBLIC_PATHS.includes(window.location.pathname)) {
    window.location.replace('/login')
  }
}

export const errorLink = onError(errorHandler)

const mainLink = new HttpLink({
  uri: `${BACKEND_BASE_URL}/graphql`,
})
const analyticsLink = new HttpLink({
  uri: `${BACKEND_BASE_URL}/analytics/graphql`,
})

export const httpLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      ['Analytics', 'Search'].some((prefix) => definition.name?.value.startsWith(prefix))
    )
  },
  analyticsLink,
  mainLink,
)

export const retryLink = new RetryLink({
  attempts: {
    max: Number.POSITIVE_INFINITY,
    retryIf: (error) => {
      const isNetworkError = error.message === 'Failed to fetch'
      const isOffline = !navigator.onLine
      return isNetworkError && isOffline
    },
  },
  delay: {
    initial: RETRY_REQUEST_INITIAL_DELAY,
    max: RETRY_REQUEST_MAX_DELAY,
    jitter: true,
  },
})

export const wsLink = new GraphQLWsLink(
  createClient({
    url: `${WS_BASE_URL}/live/graphql`,
    retryAttempts: Number.POSITIVE_INFINITY,
    retryWait: (retries: number) => {
      const delay = Math.min(RETRY_WS_INITIAL_DELAY * 2 ** retries, RETRY_WS_MAX_DELAY)
      return new Promise((resolve) => setTimeout(resolve, delay))
    },
    shouldRetry: () => true,
    keepAlive: WS_KEEP_ALIVE,
    on: {
      connected: () => {
        console.log('Connected to WebSocket')
        GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_NETWORK_ERROR_MESSAGE, false)
      },
      closed: () => {
        console.log('WebSocket connection closed')
        GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_NETWORK_ERROR_MESSAGE, true)
      },
      pong: () => {
        GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_NETWORK_ERROR_MESSAGE, false)
      },
      error: (e) => {
        console.error('WebSocket error', e)
        GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_NETWORK_ERROR_MESSAGE, true)
      },
    },
  }),
)

export const mainSplitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink,
)
